// src/lib/ai.js — AI-hulp: rol herkennen, zwaarte adviseren, onderhandelpunten signaleren,
// timesheet-suggestie, uitleg.

import { claude } from "./ai-adapter";
import { config } from "./config";
import { buildFestivalFamilies } from "./festival-families";

const isAbort = (e) => e?.name === "AbortError";

export const aiHelp = {
  async suggestRol(omschrijving, isFestival, opts) {
    // Festival: dedupliceer variant-rijen tot families — de variant-letter
    // (A t/m E) wordt in step2 bepaald via de variant-keuze of criteria-modal.
    // De AI suggereert alleen de familienaam (zonder letter).
    const rollen = isFestival
      ? buildFestivalFamilies(config.rollen_festival)
      : config.rollen_film_av;
    const list = rollen.map(r => {
      const niveaus = Array.isArray(r.niveaus) && r.niveaus.length ? r.niveaus.join("/") : (r.niveau || "");
      const aliasArr = Array.isArray(r.aliases) ? r.aliases : [];
      const aliasHint = aliasArr.length ? ` — alternatieve titels: ${aliasArr.join(", ")}` : "";
      return `- ${r.rol} (${r.dep}, niveau ${niveaus})${aliasHint}`;
    }).join("\n");
    const festivalNoot = isFestival
      ? `\n\nBelangrijk voor filmfestivals: geef de canonieke familienaam zonder variant-letter (bv. "Medewerker Productie", niet "Medewerker Productie B"). De juiste variant (A t/m E, afhankelijk van de familie) wordt later bepaald via de criteria uit het Functiehandboek Filmfestivals. Het veld "niveau" mag je leeg laten ("") of de range uit de lijst overnemen.`
      : "";
    const prompt = `Je helpt iemand in de Nederlandse film/AV-sector hun eigen rol te herkennen op basis van een vrije omschrijving van hun opdracht.

Belangrijk: de omschrijving is geschreven door de freelancer zelf, over wat ZIJ gaan doen. Andere functies die in de tekst worden genoemd (opdrachtgevers, collega's, leidinggevenden zoals een line producer, regisseur of festivalmanager) zijn NIET de rol van de gebruiker — dat zijn mensen met wie ze samenwerken of aan wie ze rapporteren. Identificeer uitsluitend de rol van de persoon die schrijft.

Sommige rollen hebben "alternatieve titels" — dat zijn werknamen die festivalorganisaties of producties in de praktijk gebruiken (bv. "Festival Producer (IDFA)" voor Medewerker Productie). Als de gebruiker zo'n alternatieve titel noemt, geef dan de canonieke rol-naam terug in je antwoord, niet de alternatieve titel.${festivalNoot}

Lijst van mogelijke rollen:
${list}

Omschrijving van de gebruiker:
"${omschrijving}"

Geef JSON terug met:
{
  "top_rollen": [
    { "rol": "exacte rol-naam uit de lijst", "dep": "departement", "niveau": "A-K of 1-12 (mag leeg voor festival)", "reden": "korte uitleg (1 zin)" }
  ],
  "toelichting": "1-2 zinnen over waarom deze rollen passen, in het Nederlands, informeel (je-vorm)"
}

Geef maximaal 3 rollen terug, meest passend eerst. Alleen JSON, geen markdown.`;

    try {
      const text = await claude.complete(prompt, opts);
      const jsonMatch = text.match(/\{[\s\S]*\}/);
      if (!jsonMatch) return null;
      return JSON.parse(jsonMatch[0]);
    } catch (e) {
      if (isAbort(e)) return null;
      console.error("AI suggestRol failed", e);
      return null;
    }
  },

  async suggestZwaarte(omschrijving, rol, isFestival, opts, niveauAankers) {
    const schaalUitleg = isFestival
      ? `Het Functiehandboek Filmfestivals hanteert zwaarteschalen 1 t/m 12 waarbij:
schaal 3   = operationeel (uitvoerend, duidelijke instructies) — bijv. Runner / Locatiemedewerker, Technisch assistent
schaal 4-5 = uitvoerend met eigen inbreng — bijv. Programma-assistent, Productiemedewerker, Social Media Medewerker
schaal 6-7 = coördinerend (coördinator, programmeur) — bijv. Programmeur, Productiecoördinator, Producent
schaal 8   = hoofd van een afdeling — bijv. Hoofd Programmering, Hoofd Productie, Hoofd Marketing
schaal 9   = directie (artistiek/zakelijk leider) — bijv. Festivaldirecteur, Artistiek Leider, Zakelijk Leider
schaal 11-12 = strategisch (algemeen/artistiek directeur op top-niveau)`
      : `Het Opdracht Kompas hanteert zwaarteniveaus A t/m K waarbij:
A     = operationeel instap (wettelijk minimumloon) — bijv. Art Department Runner
B     = operationeel (uitvoerend, duidelijke instructies) — bijv. Productie runner, Naaister, Verkeersregelaar
D,E   = tactisch/operationeel (uitvoerend met eigen inbreng, assisteert senior) — bijv. 3rd AD / Junior Schrijver (D), Productieassistent / Script-continuiteit / Dialect Coach (E)
F,G   = tactisch (zelfstandig, eigen vakgebied) — bijv. 2nd AD / Best Boy Electric (F), Researcher / Transportmanager / Dramaturg (G)
H,I   = strategisch/tactisch (leidt departement, strategische keuzes) — bijv. 1st AD / Productieleider / Art Director (H), Sound Designer / (Post)production Supervisor (I)
J,K   = strategisch (eindverantwoordelijk, hoog afbreukrisico) — bijv. Line Producer / Casting Director (J), Showrunner / Regisseur speelfilm (K)`;

    const ankerLijst = Array.isArray(niveauAankers)
      ? niveauAankers.filter(Boolean)
      : (niveauAankers ? [niveauAankers] : []);
    const ankerZin = ankerLijst.length === 0
      ? `Welk niveau past het beste bij deze opdracht?`
      : ankerLijst.length === 1
        ? `De Gids Rollen kent aan de rol "${rol}" standaard niveau ${ankerLijst[0]} toe. Beoordeel of de omschrijving dit bevestigt, of dat er een duidelijke reden is om één niveau af te wijken.`
        : `De Gids Rollen kent aan de rol "${rol}" niveau ${ankerLijst.join(" of ")} toe — kies bij voorkeur één van die niveaus. Wijk hier alleen van af als de omschrijving daar duidelijk aanleiding toe geeft.`;

    const formaat = isFestival
      ? `EXACT één getal van 1 t/m 12 (bv. "5", "7", "9"), géén range`
      : `EXACT één hoofdletter A t/m K (bv. "G", "I"), géén range zoals "F-G"`;

    const prompt = `Een freelancer ${isFestival ? "bij een Nederlands filmfestival" : "in de Nederlandse film/AV-sector"} werkt aan de rol "${rol}". De opdracht wordt zo omschreven:

"${omschrijving}"

${schaalUitleg}

${ankerZin} Kies één enkel niveau.

Geef JSON:
{
  "niveau": "${formaat}",
  "reden": "2-3 zinnen in gewone taal, Nederlands, je-vorm, waarom dit niveau",
  "twijfel": "optioneel: 1 zin waarom het ${isFestival ? "een schaal" : "een niveau"} hoger of lager zou kunnen zijn"
}

Belangrijk: het veld "niveau" moet ${formaat}. Niet meerdere niveaus combineren. Als je twijfelt, kies één en zet de andere in "twijfel". Alleen JSON, geen markdown.`;

    try {
      const text = await claude.complete(prompt, opts);
      const jsonMatch = text.match(/\{[\s\S]*\}/);
      if (!jsonMatch) return null;
      return JSON.parse(jsonMatch[0]);
    } catch (e) {
      if (isAbort(e)) return null;
      console.error("AI suggestZwaarte failed", e);
      return null;
    }
  },

  async signalOnderhandelpunten(omschrijving, opts, urenContext) {
    let urenTekst = "";
    if (urenContext) {
      const { rol, niveau, uren, urenDetails } = urenContext;
      const totaal = (uren.voorbereiding || 0) + (uren.hoofdwerk || 0) + (uren.afronding || 0) + (uren.pr_promotie || 0);
      const detailRegel = (cat, label) => {
        if (!uren[cat]) return "";
        const rijen = Array.isArray(urenDetails?.[cat])
          ? urenDetails[cat].filter(d => d.activiteit && (Number(d.uren) || 0) > 0)
          : [];
        const detail = rijen.length ? ` (${rijen.map(r => `${r.activiteit}: ${r.uren}u`).join(", ")})` : "";
        return `\n- ${label}: ${uren[cat]} uur${detail}`;
      };
      urenTekst = `\n\nDe freelancer werkt als ${rol} op niveau ${niveau}. Ingevulde tijdsinvestering (${totaal} uur totaal):${detailRegel("voorbereiding", "Voorbereiding")}${detailRegel("hoofdwerk", "Hoofdwerkzaamheden")}${detailRegel("afronding", "Afronding")}${detailRegel("pr_promotie", "PR & promotie")}`;
    }

    const prompt = `Een freelancer beschrijft een opdracht in de Nederlandse film/AV-sector:

"${omschrijving}"${urenTekst}

Signaleer factoren die volgens de Handleiding fair pay aanleiding kunnen zijn om over het openingsbod in onderhandeling te treden. Mogelijke factoren:
- Complexiteit van de opdracht
- Specifieke ervaring of specialisme dat gevraagd wordt
- Aard van de productie (hoog afbreukrisico, groot publiek, gevoelig onderwerp)
- Ongebruikelijke werktijden of -locaties (nachtopnames, extreme locaties, meerdere steden)
- Looptijd / beschikbaarheidsvereiste (exclusiviteit over langere periode)
- Psychosociale belasting (zwaar thematisch materiaal — verwijs indien relevant naar Leidraad veilig en gezond werken)
- Hoog aantal reiskilometers of apart te offreren posten die onderhandelruimte aangeven

Geef JSON:
{
  "signalen": [
    {
      "factor": "korte naam (max 4 woorden)",
      "toelichting": "1 zin waarom dit relevant is voor onderhandeling",
      "leidraad_verwijzing": "optioneel: onderwerp uit Leidraad veilig werken"
    }
  ]
}
Maximaal 4 signalen. Geen euro-bedragen, geen percentages. Alleen JSON, geen markdown.`;

    try {
      const text = await claude.complete(prompt, opts);
      const jsonMatch = text.match(/\{[\s\S]*\}/);
      if (!jsonMatch) return null;
      return JSON.parse(jsonMatch[0]);
    } catch (e) {
      if (isAbort(e)) return null;
      console.error("AI signalOnderhandelpunten failed", e);
      return null;
    }
  },

  async suggestTimesheetUren(omschrijving, rol, niveau, sjabloon, opts) {
    if (!sjabloon) return null;
    const cats = ["hoofdwerk", "voorbereiding", "afronding", "pr_promotie"];
    const activiteitenLijst = cats
      .filter(cat => Array.isArray(sjabloon[cat]) && sjabloon[cat].length)
      .map(cat => `${cat}:\n` + sjabloon[cat].map(a => `  - ${a}`).join("\n"))
      .join("\n\n");

    const rolZin = niveau
      ? `Ze werken als ${rol} op niveau ${niveau}.`
      : `Ze werken als ${rol}. Het zwaarteniveau is nog niet gekozen — focus op een schatting van uren op basis van de omschrijving.`;
    const prompt = `Een freelancer in de Nederlandse film/AV-sector beschrijft een opdracht:

"${omschrijving}"

${rolZin} Voor dit departement zijn de volgende werkzaamheden relevant:

${activiteitenLijst}

Schat op basis van de omschrijving welke activiteiten waarschijnlijk van toepassing zijn en hoeveel uren elk zou kosten. Reken aan de voorzichtige kant — je geeft een head-start, de gebruiker past zelf aan.

Geef JSON:
{
  "suggesties": {
    "hoofdwerk": [
      { "activiteit": "exacte naam uit de lijst", "uren": 40, "reden": "1 zin waarom dit aantal" }
    ],
    "voorbereiding": [],
    "afronding": [],
    "pr_promotie": []
  },
  "toelichting": "1-2 zinnen over de schatting als geheel, Nederlands, je-vorm"
}

Voorwaarden:
- Alleen activiteiten die duidelijk uit de omschrijving volgen
- Het veld "activiteit" moet de naam EXACT bevatten zoals in de lijst — inclusief alles tussen haakjes en alle interpunctie. Voorbeeld goed: "Research (gesprekken voeren, bekijken van bestaande filmwerken, ...)". Voorbeeld FOUT: "Research" (afgekort, mist de haakjes-uitleg).
- Uren zijn hele getallen
- Geef per categorie max 5 activiteiten
- Laat een categorie leeg ([]) als er geen info is
- Alleen JSON, geen markdown`;

    try {
      const text = await claude.complete(prompt, opts);
      const jsonMatch = text.match(/\{[\s\S]*\}/);
      if (!jsonMatch) return null;
      return JSON.parse(jsonMatch[0]);
    } catch (e) {
      if (isAbort(e)) return null;
      console.error("AI suggestTimesheetUren failed", e);
      return null;
    }
  },

  async uitleg(aanname, opts) {
    const prompt = `Leg in 2 zinnen gewone taal (Nederlands, je-vorm, warm maar helder) uit waarom deze aanname gemaakt is in een Fair Pay berekening voor de film/AV-sector:

Aanname: ${aanname.label}
Gekozen waarde: ${aanname.waarde}
Context: ${aanname.context}

Geef alleen de uitleg, geen intro. Maximaal 40 woorden.`;

    try {
      return await claude.complete(prompt, opts);
    } catch (e) {
      if (isAbort(e)) return null;
      console.error("AI uitleg failed", e);
      return "Deze aanname volgt uit de Fair Pay richtlijn van de sector.";
    }
  },
};
